import type { IAppConfigResponse } from '@modules/config/types';
import type { IContextMutationResult } from '@modules/core/services/query/types';
import { CONFIG_QUERY_KEY } from '@modules/config/constants';
import { isServerValidationError } from '@modules/core/errors';
import { useRestClient } from '@modules/core/services/api';
import { useContextMutation } from '@modules/core/services/query';
import { useI18nStore } from '@modules/i18n/store';
import { PERMISSIONS_QUERY_KEY } from '@modules/permissions/constants';
import { useToastStore } from '@modules/toast/store';

import type { ILoginPayload, ILoginResponse, IUserLoginRequiredMfaCode } from '../types';
import {
  USER_SIGN_IN_MUTATION_KEY,
  USER_SIGN_IN_URL,
} from '../constants';

export default function useUserSignInMutation(): IContextMutationResult<ILoginResponse, Error, ILoginPayload> {
  const { fetchPost } = useRestClient();
  const { showErrorsWithToast } = useToastStore();
  const { setLocale } = useI18nStore();

  return useContextMutation<ILoginResponse, Error, ILoginPayload>({
    mutationKey: [USER_SIGN_IN_MUTATION_KEY],
    mutationFn: (user) => fetchPost<ILoginResponse>(USER_SIGN_IN_URL, { user }),
    showErrorsInToast: false,
    async onSuccess({ data: response, queryClient }) {
      await queryClient.invalidateQueries({ queryKey: [PERMISSIONS_QUERY_KEY] });

      queryClient.setQueryData<IAppConfigResponse>([CONFIG_QUERY_KEY], (data) => {
        if (!data) {
          return data;
        }

        return {
          ...data,
          current_user: response.user,
        };
      });

      if (response?.user?.locale) {
        await setLocale(response.user.locale);
      }
    },
    onError(error) {
      if (isServerValidationError<object, IUserLoginRequiredMfaCode>(error) && error.response.required_mfa_code) {
        return;
      }
      showErrorsWithToast(error);
    },
  });
}
